import React, { createContext, ReactNode, useCallback, useContext, useEffect } from 'react';
import { useStore } from '../hooks/useStore';
import { ChatbotConfig, useChatbotConfigStore } from '../stores/chatbotConfigStore';
import { Json } from '../types/supabase';
import { useChatbot } from './ChatbotContext';

type ConfigSectionKey = keyof Omit<ChatbotConfig, 'id' | 'chatbot_public_id' | 'created_at' | 'updated_at'>;

type ChatbotConfigContextType = {
  config: ChatbotConfig | null;
  isLoading: boolean;
  error: Error | null;
  updateConfigSection: <T extends object | string | number | boolean | null,>(
    sectionKey: ConfigSectionKey,
    data: T
  ) => Promise<boolean>;
  getConfigSection: <T,>(
    sectionKey: ConfigSectionKey
  ) => Promise<T | null>;
  updateConfigField: (
    sectionKey: ConfigSectionKey,
    field: string,
    value: Json | null
  ) => Promise<boolean>;
  refetchConfig: () => Promise<ChatbotConfig | null>;
};

const ChatbotConfigContext = createContext<ChatbotConfigContextType | undefined>(undefined);

type ChatbotConfigProviderProps = {
  children: ReactNode;
};

export const ChatbotConfigProvider: React.FC<ChatbotConfigProviderProps> = ({ children }) => {
  const { chatbotPublicId } = useChatbot();
  const { setInitialized } = useStore();
  const { 
    configs, 
    isLoading, 
    error, 
    fetchConfig, 
    getConfigSection: getConfig, 
    updateConfigSection: updateConfig 
  } = useChatbotConfigStore();

  // Fetch the config when chatbotPublicId changes
  useEffect(() => {
    const fetchChatbotConfig = async () => {
      if (chatbotPublicId) {
        const config = await fetchConfig(chatbotPublicId);
        if (config) {
          setInitialized(true);
        }
      }
    };

    fetchChatbotConfig();
  }, [chatbotPublicId, fetchConfig, setInitialized]);

  // Get the config for the current chatbot
  const config = chatbotPublicId ? configs[chatbotPublicId] || null : null;

  // Refetch config
  const refetchConfig = useCallback(() => {
    if (!chatbotPublicId) return Promise.resolve(null);
    return fetchConfig(chatbotPublicId, true);
  }, [chatbotPublicId, fetchConfig]);

  // Update a specific config section
  const updateConfigSection = useCallback(
    <T extends object | string | number | boolean | null,>(
      sectionKey: ConfigSectionKey,
      data: T
    ): Promise<boolean> => {
      if (!chatbotPublicId) {
        console.error('No chatbot ID available for config update');
        return Promise.resolve(false);
      }
      return updateConfig(sectionKey, data, chatbotPublicId);
    },
    [chatbotPublicId, updateConfig]
  );

  // Get a specific config section
  const getConfigSection = useCallback(
    <T,>(sectionKey: ConfigSectionKey): Promise<T | null> => {
      if (!chatbotPublicId) {
        return Promise.resolve(null);
      }
      return getConfig<T>(sectionKey, chatbotPublicId);
    },
    [chatbotPublicId, getConfig]
  );

  // Update a specific field within a section
  const updateConfigField = useCallback(
    async (
      sectionKey: ConfigSectionKey,
      field: string,
      value: Json | null
    ): Promise<boolean> => {
      if (!chatbotPublicId) {
        console.error('No chatbot ID available for field update');
        return false;
      }

      // Get current section data
      const sectionData = await getConfig<Record<string, Json | null>>(sectionKey, chatbotPublicId) || {};
      
      // Update the field in the section data
      const updatedSection = { 
        ...sectionData, 
        [field]: value 
      };

      // Update the entire section
      return updateConfig(sectionKey, updatedSection, chatbotPublicId);
    },
    [chatbotPublicId, getConfig, updateConfig]
  );

  const value: ChatbotConfigContextType = {
    config,
    isLoading,
    error,
    updateConfigSection,
    getConfigSection,
    updateConfigField,
    refetchConfig
  };

  return (
    <ChatbotConfigContext.Provider value={value}>
      {children}
    </ChatbotConfigContext.Provider>
  );
};

export const useChatbotConfig = (): ChatbotConfigContextType => {
  const context = useContext(ChatbotConfigContext);
  if (context === undefined) {
    throw new Error('useChatbotConfig must be used within a ChatbotConfigProvider');
  }
  return context;
}; 